/* ==========================================================================
   bb-video.js — the shoppable clips under Add to cart on the product page.

   Two jobs.

   1. THE THUMBNAIL LOOPS. Liquid renders each thumbnail as a muted <video>
      with NO src — the URL rides on data-bb-src (data-src is claimed by the theme's lazy-loader). Nothing downloads until the row
      is actually on screen, and then only for the thumbnails that are. A
      shopper who never scrolls past the buy button pays nothing for this
      feature. Playback also stops when the tab is hidden, when the viewer is
      open over the top, and when the row scrolls away again.

      Three conditions suppress the loops entirely, leaving the poster frame:
      prefers-reduced-motion, Save-Data, and a browser that refuses the
      autoplay (older iOS in Low Power Mode does). The poster is a still from
      the video, so the row looks intentional either way — this degrades to
      a plain image grid, not to three black rectangles.

   2. THE EXPANDED VIEWER. Choosing a clip, loading its sources on demand,
      moving between clips, and handing "Add to cart" back to the real
      product form.
   ========================================================================== */
(function () {
  'use strict';
  /* Both sections include this file; run once per page. */
  if (window.__bbVideoLoaded) return;
  window.__bbVideoLoaded = true;

  /* Both are read once. Save-Data does not change mid-session, and a
     reduced-motion change fires a full re-evaluation through matchMedia
     below rather than needing a re-read here. */
  var reduceMotion = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false, addEventListener: null };

  function saveData() {
    var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    return !!(c && c.saveData);
  }

  function init(root) {
    if (!root || root.dataset.bbClipsBound) return;
    root.dataset.bbClipsBound = 'true';

    var viewer = root.querySelector('[data-bb-clipviewer]');
    var video = root.querySelector('[data-bb-clip-video]');
    var titleEl = root.querySelector('[data-bb-clip-title]');
    var countEl = root.querySelector('[data-bb-clip-count]');
    var dataEl = root.querySelector('[data-bb-clip-data]');
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-bb-clip-loop]'));

    /* ----------------------------------------------------------------------
       PORTAL THE VIEWER TO <body>.

       The viewer is `position: fixed; z-index: 1100`, which only means
       "above everything" while it is a child of the root stacking context.
       It is authored INSIDE the clips root so this file can find it, and on
       Custom & Events that root sits inside `.bb-enquiry__copy`, which is
       `position: sticky` — and a sticky element CREATES A STACKING CONTEXT
       whatever its z-index. That capped the whole viewer at the sticky
       column's level, so the open modal painted UNDERNEATH the page: probing
       the panel's centre returned .bb-custom-hero__body, its top-left
       returned the H1, and the scrim returned a nav link.

       Re-parenting to <body> is the standard fix and it is safe here: every
       reference above is already held as an element, so nothing re-queries,
       and all the viewer's CSS is flat class selectors that never depend on
       an ancestor. It also immunises the other three surfaces against any
       future transform/filter/sticky wrapper, which is the same trap.

       dataEl deliberately stays put — it is read once, below, and is a
       sibling of the viewer rather than a child.
       ---------------------------------------------------------------------- */
    if (viewer && viewer.parentNode !== document.body) {
      viewer.setAttribute('data-bb-clip-portaled', '');
      viewer._bbClipsRoot = root;
      document.body.appendChild(viewer);
    }

    /* ----------------------------------------------------------------------
       1. Thumbnail loops
       ---------------------------------------------------------------------- */

    var inView = [];        /* thumbnails currently on screen */
    var suppressed = document.visibilityState === 'hidden'; /* viewer open, or tab hidden */

    function allowLoops() {
      return !reduceMotion.matches && !saveData();
    }

    /* A thumbnail is IDLE when it is showing a still it will NOT animate out
       of: motion is off at the OS or browser level, or this clip's play() was
       refused. Paused because it scrolled off screen, or because the viewer
       opened, is deliberately NOT idle — those resume on their own, and
       flashing a play button through them would be noise.

       This drives the idle-only play glyph (see snippets/bb-clip-thumb). The
       glyph used to be removed in Liquid whenever `hide_play` was set, which
       meant a shopper on reduced motion, Save-Data or Low Power Mode got a
       still picture with nothing to press and no way to reach the clip. */
    /* The glyph is VISIBLE BY DEFAULT and hidden only by proof that pixels are
       moving. That direction is deliberate.

       It used to be the other way round — `hide_play` deleted the glyph in
       Liquid — and the failure mode was silent: reduced motion, Save-Data, iOS
       Low Power Mode, a refused play(), a stalled source or an observer that
       never fired all leave a still frame on screen, and there was then nothing
       to press. An earlier attempt at this fix armed a timer inside playThumb,
       which was no better: if the observer never fires, playThumb never runs
       and the timer is never set. Anything that keys off the script SUCCEEDING
       cannot rescue the case where the script does nothing.

       So CSS shows the glyph after a short delay unless this flag says the clip
       is playing, and the flag is only ever set by the `playing` event. If this
       whole file fails to run, the glyph is simply there — which is the safe
       direction to fail in.

       Scroll-out and viewer-open pauses deliberately do NOT clear it: those
       resume on their own, and flashing a button through them is the furniture
       `hide_play` exists to avoid. Only terminal states clear it. */
    function markPlaying(v, playing) {
      var thumb = v.closest ? v.closest('.bb-clips__thumb') : null;
      if (!thumb) return;
      if (playing) thumb.setAttribute('data-bb-clip-playing', '');
      else thumb.removeAttribute('data-bb-clip-playing');
    }

    /* Attach the source the first time this thumbnail is needed and not
       again. `load()` is required — a <video> that had no src at parse time
       will not pick one up without it. */
    function attach(v) {
      if (v.dataset.bbClipReady) return;
      var src = v.getAttribute('data-bb-src');
      if (!src) return;

      /* The 480p rung in data-src is a GUESS. Shopify puts the bitrate its
         encoder happened to choose into the filename, and that differs per
         upload — SD-480p-1.5Mbps on one clip, 1.0 or 0.9 on another — so the
         right URL cannot be derived from the 1080p one. bb-clip-video emits
         the plausible spellings cheapest-first in data-src-fallback, ending
         with the URL Liquid actually gave us, which is always real. A miss
         costs one 404 and we step down the list. */
      var listed = (v.getAttribute('data-bb-src-fallback') || '').split(' ');
      var alts = [];
      for (var i = 0; i < listed.length; i++) {
        if (listed[i]) alts.push(listed[i]);
      }
      v.bbAlts = alts;

      /* Bound once, here, because this is the only place that runs exactly
         once per video. `playing` is the single honest signal that pixels are
         moving — it is what hides the glyph again, not play() resolving, which
         only means the request was accepted. */
      v.addEventListener('playing', function () { markPlaying(v, true); });
      v.addEventListener('error', function () {
        if (v.bbAlts && v.bbAlts.length) {
          v.src = v.bbAlts.shift();
          v.load();
          /* Only chase it if the tile is still on screen and still allowed to
             move. Otherwise leave it loaded and let syncThumbs decide later. */
          if (inView.indexOf(v) !== -1 && !suppressed && allowLoops()) {
            var p = v.play();
            if (p && p.then) p.then(null, function () {});
          }
          return;
        }
        /* Nothing left to try. The poster stays up — a still frame, not a
           black rectangle. */
        markPlaying(v, false);
      });

      v.src = src;
      v.dataset.bbClipReady = 'true';
      v.load();
    }

    function playThumb(v) {
      /* Per-tile opt-out (a collection frame with "Play automatically" off).
         Same outcome as motion being switched off globally: it never moves, so
         the glyph stays up and the tile is still openable. */
      if (v.hasAttribute('data-bb-clip-noautoplay')) { markPlaying(v, false); return; }
      if (!allowLoops()) { markPlaying(v, false); return; }
      if (suppressed) return;
      attach(v);
      var p = v.play();
      if (p && p.then) {
        p.then(null, function () {
          /* A play() that rejects because the src 404d is the fallback
             ladder's business — the error handler is already stepping down.
             Only give up once there is genuinely nothing left to try. */
          if (v.bbAlts && v.bbAlts.length) return;
          /* Refused. The poster stays up and we stop asking — retrying on
             every scroll would just burn cycles for the same answer. */
          v.dataset.bbClipRefused = 'true';
          markPlaying(v, false);
        });
      }
    }

    function syncThumbs() {
      inView.forEach(function (v) {
        if (suppressed || !allowLoops()) {
          if (typeof v.pause === 'function') v.pause();
          /* Suppressed is transient — the viewer is open on top, or the tab is
             hidden. Nobody is looking, and it resumes by itself. */
          if (!allowLoops()) markPlaying(v, false);
        } else if (v.dataset.bbClipRefused) {
          markPlaying(v, false);
        } else {
          playThumb(v);
        }
      });
    }

    if (thumbs.length) {
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            var v = entry.target;
            var at = inView.indexOf(v);
            if (entry.isIntersecting) {
              if (at === -1) inView.push(v);
              playThumb(v);
            } else {
              if (at !== -1) inView.splice(at, 1);
              if (typeof v.pause === 'function') v.pause();
            }
          });
        }, {
          /* Half the thumbnail visible. A clip that is one pixel into the
             viewport is not something anyone is watching. rootMargin gives
             it a moment to buffer before it is actually looked at. */
          threshold: 0.5,
          rootMargin: '100px 0px'
        });
        /* Start watching only after the page has loaded and the browser is
           idle: the clips row sits inside the first screen on phones, so an
           immediate observe would pull ~500 KB of previews in the same second
           as the product photo. */
        var observeAll = function () { thumbs.forEach(function (v) { io.observe(v); }); };
        var whenIdle = function () {
          if ('requestIdleCallback' in window) requestIdleCallback(observeAll, { timeout: 2500 });
          else setTimeout(observeAll, 800);
        };
        if (document.readyState === 'complete') whenIdle();
        else window.addEventListener('load', whenIdle, { once: true });
      } else {
        /* No IntersectionObserver: attach and play everything once. This is
           the old-Safari path, and there are three short clips at most. */
        thumbs.forEach(function (v) { inView.push(v); playThumb(v); });
      }

      /* A hidden tab should not be decoding video. */
      document.addEventListener('visibilitychange', function () {
        suppressed = document.visibilityState === 'hidden' || !viewerClosed();
        syncThumbs();
      });

      /* Someone turning reduced-motion on mid-session gets it honoured. */
      if (reduceMotion.addEventListener) {
        reduceMotion.addEventListener('change', syncThumbs);
      } else if (reduceMotion.addListener) {
        reduceMotion.addListener(syncThumbs);
      }
    }

    /* ----------------------------------------------------------------------
       2. The viewer
       ---------------------------------------------------------------------- */

    function viewerClosed() {
      return !viewer || viewer.hasAttribute('hidden');
    }

    if (!viewer || !video || !dataEl) return;

    var clips = [];
    try {
      clips = JSON.parse(dataEl.textContent) || [];
    } catch (e) {
      /* Malformed JSON means the row still works as thumbnails; only the
         viewer is lost. Failing quietly beats throwing on every PDP. */
      return;
    }
    if (!clips.length) return;

    var index = 0;
    var lastFocus = null;

    function show(i) {
      if (i < 0) i = clips.length - 1;
      if (i >= clips.length) i = 0;
      index = i;
      var clip = clips[index];

      /* An IMAGE tile. The Instagram grid mixes stills with clips, so the stage
         swaps between the two rather than the section carrying two viewers. */
      var still = viewer.querySelector('[data-bb-clip-image]');
      if (clip.type === 'image') {
        video.pause();
        while (video.firstChild) video.removeChild(video.firstChild);
        video.removeAttribute('src');
        video.removeAttribute('poster');
        video.hidden = true;
        if (still) { still.src = clip.image || ''; still.alt = clip.caption || ''; still.hidden = false; }
        if (titleEl) titleEl.textContent = clip.caption || '';
        if (countEl) countEl.textContent = 'Post ' + (index + 1) + ' of ' + clips.length;
        label(clip.caption || ('Post ' + (index + 1) + ' of ' + clips.length));
        paint(clip.product);
        permalink(clip.permalink);
        return;
      }
      if (still) { still.hidden = true; still.removeAttribute('src'); }
      video.hidden = false;

      video.pause();
      while (video.firstChild) video.removeChild(video.firstChild);
      video.removeAttribute('src');

      if (clip.poster) video.setAttribute('poster', clip.poster);
      else video.removeAttribute('poster');
      (clip.sources || []).forEach(function (s) {
        if (!s || !s.url) return;
        var el = document.createElement('source');
        el.src = s.url;
        if (s.mime) el.type = s.mime;
        video.appendChild(el);
      });

      video.load();
      var played = video.play();
      if (played && played.catch) played.catch(function () { /* autoplay may be refused; controls are there */ });

      if (titleEl) titleEl.textContent = clip.caption || '';
      if (countEl) countEl.textContent = 'Clip ' + (index + 1) + ' of ' + clips.length;
      label(clip.caption || ('Clip ' + (index + 1) + ' of ' + clips.length));

      paint(clip.product);
      permalink(clip.permalink);
    }

    /* The dialog's accessible name: the caption when there is one, else the position. */
    function label(text) {
      var dlg = viewer.querySelector('[role="dialog"]');
      if (dlg) dlg.setAttribute('aria-label', text);
    }

    /* The link to the real Instagram post, when the tile carries one. */
    function permalink(url) {
      var a = viewer.querySelector('[data-bb-clip-permalink]');
      if (!a) return;
      if (url) { a.href = url; a.hidden = false; }
      else { a.removeAttribute('href'); a.hidden = true; }
    }

    /* Every clip carries its own product, so the buy panel is rebuilt on each
       show rather than rendered once. A Customizer clip can be attached to any
       product; a clip from a product's own metafield carries that product. */
    function paint(p) {
      var link = viewer.querySelector('[data-bb-clip-product]');
      var cards = Array.prototype.slice.call(viewer.querySelectorAll('[data-bb-clip-card]'));
      var addBtns = Array.prototype.slice.call(viewer.querySelectorAll('[data-bb-clip-add]'));

      /* No product tagged on this tile: hide the whole buy panel rather than
         leave an empty card and a dead button. Grid tiles are often just a nice
         photo. */
      var has = !!(p && p.id);
      cards.forEach(function (c) { c.hidden = !has; });
      if (link) link.hidden = !has;
      addBtns.forEach(function (b) { b.hidden = !has; });

      /* "Shop now" mode (homepage grid): the button is a link to the tile's
         product, or to the section's fallback URL when nothing is tagged. It is
         always shown, and the side panel is flagged so the full-width link can
         stay visible on phones when the product card is hidden. */
      var side = viewer.querySelector('.bb-clipviewer__side');
      if (side) { if (has) side.removeAttribute('data-bb-no-product'); else side.setAttribute('data-bb-no-product', ''); }
      Array.prototype.forEach.call(viewer.querySelectorAll('[data-bb-clip-shop]'), function (a) {
        a.href = (has && p.url) || a.getAttribute('data-bb-fallback-url') || '#';
        a.hidden = false;
      });
      var img = viewer.querySelector('[data-bb-clip-img]');
      if (!has) {
        var nm = viewer.querySelector('[data-bb-clip-name]'), pr = viewer.querySelector('[data-bb-clip-price]');
        if (nm) nm.textContent = '';
        if (pr) pr.textContent = '';
        if (img) { img.removeAttribute('src'); img.hidden = true; }
        if (link) link.removeAttribute('href');
        return;
      }
      var tier = viewer.querySelector('[data-bb-clip-tier]');
      var name = viewer.querySelector('[data-bb-clip-name]');
      var price = viewer.querySelector('[data-bb-clip-price]');

      if (link) link.href = p.url || '#';
      if (img) {
        if (p.image) { img.src = p.image; img.alt = p.imageAlt || ''; img.hidden = false; }
        else { img.removeAttribute('src'); img.hidden = true; }
      }
      /* hidden, not empty text: an empty <span> still occupies its line-height. */
      if (tier) { tier.textContent = p.tier || ''; tier.hidden = !p.tier; }
      if (name) name.textContent = p.title || '';
      if (price) price.textContent = p.price || '';

      /* Two buttons share the job: the full-width one (desktop) and the compact
         one inside the product card (phone). CSS shows one per breakpoint; both
         are kept in sync here. Each remembers its own label. */
      addBtns.forEach(function (add) {
        add.disabled = !p.available;
        if (!add.dataset.bbLabel) add.dataset.bbLabel = (add.textContent || '').trim() || 'Add to cart';
        add.textContent = p.available ? add.dataset.bbLabel : 'Sold out';
      });
    }

    function open(i, trigger) {
      lastFocus = trigger || document.activeElement;
      viewer.removeAttribute('hidden');
      document.documentElement.classList.add('bb-clips-open');
      suppressed = true;
      syncThumbs();
      show(i);
      /* Must be the BUTTON, not the scrim — the scrim also carries
         data-bb-clip-close, sits first in the DOM, and is a div, so focusing
         it silently does nothing and the dialog opens with focus still out
         on the page behind it. */
      var closeBtn = viewer.querySelector('button[data-bb-clip-close]');
      if (closeBtn) closeBtn.focus();
    }

    function close() {
      video.pause();
      viewer.setAttribute('hidden', '');
      document.documentElement.classList.remove('bb-clips-open');
      suppressed = document.visibilityState === 'hidden';
      syncThumbs();
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
      lastFocus = null;
    }

    function onClipClick(e) {
      var openBtn = e.target.closest('[data-bb-clip-open]');
      if (openBtn) {
        e.preventDefault();
        open(parseInt(openBtn.getAttribute('data-bb-clip-open'), 10) || 0, openBtn);
        return;
      }
      if (e.target.closest('[data-bb-clip-close]')) { close(); return; }
      if (e.target.closest('[data-bb-clip-prev]')) { show(index - 1); return; }
      if (e.target.closest('[data-bb-clip-next]')) { show(index + 1); return; }

      var add = e.target.closest('[data-bb-clip-add]');
      if (add && !add.disabled) {
        addCurrentClip(add);
      }
    }

    /* Bound to BOTH halves. The thumbnails stay in the clips root but the
       viewer is portaled to <body>, so a click inside the viewer no longer
       bubbles through the root — delegating on the root alone silently killed
       close, prev, next and add-to-cart. The two subtrees are disjoint after
       the portal, so one handler on each cannot double-fire. */
    root.addEventListener('click', onClipClick);
    if (viewer) viewer.addEventListener('click', onClipClick);

    function status(msg) {
      var s = viewer.querySelector('[data-bb-clip-status]');
      if (s) s.textContent = msg;
    }

    function addCurrentClip(btn) {
      var p = clips[index] && clips[index].product;
      if (!p) return;

      /* THE CLIP'S PRODUCT IS THIS PAGE'S PRODUCT.
         Hand off to the real form so the shopper's variant selection is
         honoured — the form knows which variant is chosen and this does not.
         bb-ui.js intercepts the submit, adds over the Cart API, re-renders the
         drawer and opens it. The viewer closes first so two overlays are never
         stacked. */
      if (p.current) {
        /* This page's product: hand off to the real product form so the
           shopper's variant choice is honoured. Clicking the form's own submit
           button runs the theme's add-to-cart handling (and its analytics
           hooks) exactly as a normal add would. Scoped to this section first
           so another .js-product-form on the page cannot be picked up. */
        var sec = root.closest('.shopify-section');
        var form = (sec && sec.querySelector('.js-product-form')) || document.querySelector('.js-product-form');
        var submit = form && (form.querySelector('button[type="submit"]') || form.querySelector('.js-add-to-cart'));
        close();
        if (submit) submit.click();
        else if (form) {
          if (typeof form.requestSubmit === 'function') form.requestSubmit();
          else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
        return;
      }

      /* A DIFFERENT PRODUCT (no form for it on this page): add the chosen
         variant over the Cart API, then go to the cart. This theme has no
         rendered cart drawer to refresh, so the cart page is the honest
         destination. */
      if (!p.variantId) return;
      var label = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding\u2026';
      status('Adding ' + (p.title || 'item') + ' to your cart.');

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: p.variantId, quantity: 1 }] })
      })
        .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
        .then(function () {
          close();
          window.location.href = '/cart';
        })
        .catch(function () {
          btn.disabled = false;
          btn.textContent = label;
          status('That could not be added. Please try again.');
        });
    }

    document.addEventListener('keydown', function (e) {
      if (viewer.hasAttribute('hidden')) return;
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key === 'ArrowLeft' && clips.length > 1) { show(index - 1); return; }
      if (e.key === 'ArrowRight' && clips.length > 1) { show(index + 1); return; }
      if (e.key === 'Tab') {
        /* Keep focus inside the dialog: the page behind it is scroll-locked. */
        var items = Array.prototype.filter.call(
          viewer.querySelectorAll('button, a[href], video[controls], [tabindex]:not([tabindex="-1"])'),
          function (el) { return !el.disabled && !el.closest('[hidden]') && el.getClientRects().length; }
        );
        if (!items.length) return;
        var first = items[0], last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });
  }

  function boot() {
    /* A theme-editor re-render can replace a section while its viewer is open. */
    document.documentElement.classList.remove('bb-clips-open');
    /* A theme-editor re-render replaces the section subtree, so the root that
       owned a portaled viewer can vanish while the viewer itself — now a child
       of <body> — survives. Drop those before binding, or each save leaves
       another orphan behind. */
    document.querySelectorAll('body > [data-bb-clip-portaled]').forEach(function (v) {
      if (!v._bbClipsRoot || !v._bbClipsRoot.isConnected) v.remove();
    });
    document.querySelectorAll('[data-bb-clips]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  /* Re-bind after a section re-render in the theme editor. */
  document.addEventListener('shopify:section:load', boot);
})();
